/**
 * BusinessInfoStep Component
 *
 * Step 2 of the vendor registration wizard.
 * Collects business information: business name, vendor type, phone, email.
 * Includes business name availability check on blur.
 */

import { useState, useCallback, useEffect } from 'react';
import { Stack, Group } from '@/components/ui/layout';
import { Text, Button } from '@/components/ui/core';
import { TextInput, Select } from '@/components/ui/inputs';
import { Alert } from '@/components/ui/feedback';
import { IconBuilding, IconPhone, IconMail, IconAlertCircle, IconWorld } from '@tabler/icons-react';
import { validators } from '@/utils/validation';
import { useVendorsServices } from '@actschurch/ccms-sdk/react';
import { useDebouncedCallback } from '@mantine/hooks';

export interface BusinessInfoData {
  businessName: string;
  vendorTypeID: number;
  phoneNumber: string;
  email: string;
  website: string;
}

interface BusinessInfoStepProps {
  data: BusinessInfoData;
  onDataChange: (data: Partial<BusinessInfoData>) => void;
  onNext: () => void;
  onBack?: () => void;
  /** Whether to show the back button (default: true) */
  showBackButton?: boolean;
}

interface VendorTypeOption {
  typeID: number;
  name: string;
}

/**
 * Format phone number as user types
 * Allows +, digits, spaces, and common separators
 * Format: +XX XXX XXX XXXX or similar international format
 */
function formatPhoneNumber(value: string): string {
  // Remove all non-digit characters except +
  const cleaned = value.replace(/[^\d+]/g, '');

  // If starts with +, format as international
  if (cleaned.startsWith('+')) {
    const digits = cleaned.slice(1);
    if (digits.length <= 2) return `+${digits}`;
    if (digits.length <= 5) return `+${digits.slice(0, 2)} ${digits.slice(2)}`;
    if (digits.length <= 8) return `+${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
    return `+${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 12)}`;
  }

  // Local format without country code
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
  if (cleaned.length <= 10) return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
  return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 10)}`;
}

/**
 * Normalize website URL - add https:// if missing
 */
function normalizeWebsiteUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';

  // If already has protocol, return as is
  if (trimmed.match(/^https?:\/\//i)) {
    return trimmed;
  }

  // If starts with www., prepend https://
  if (trimmed.toLowerCase().startsWith('www.')) {
    return `https://${trimmed}`;
  }

  // If contains a dot (likely a domain), prepend https://
  if (trimmed.includes('.') && !trimmed.includes(' ')) {
    return `https://${trimmed}`;
  }

  return trimmed;
}

export function BusinessInfoStep({ data, onDataChange, onNext, onBack, showBackButton = true }: BusinessInfoStepProps) {
  const vendorServices = useVendorsServices();
  const registrationService = vendorServices.vendorRegistration;
  const vendorTypeService = vendorServices.vendorType;

  // Vendor types
  const [vendorTypes, setVendorTypes] = useState<VendorTypeOption[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(true);

  // Error states
  const [businessNameError, setBusinessNameError] = useState('');
  const [vendorTypeError, setVendorTypeError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');

  // Business name check states
  const [isCheckingName, setIsCheckingName] = useState(false);
  const [nameExists, setNameExists] = useState(false);

  // Load vendor types
  useEffect(() => {
    const loadVendorTypes = async () => {
      try {
        const types = await vendorTypeService.getAll();
        setVendorTypes(types);
      } catch (err) {
        console.error('Failed to load vendor types:', err);
      } finally {
        setLoadingTypes(false);
      }
    };
    loadVendorTypes();
  }, [vendorTypeService]);

  // Debounced business name check
  const checkBusinessNameAvailability = useDebouncedCallback(async (name: string) => {
    if (!name || name.trim().length < 2) {
      setNameExists(false);
      return;
    }

    setIsCheckingName(true);
    try {
      const exists = await registrationService.checkBusinessName(name);
      setNameExists(exists);
      if (exists) {
        setBusinessNameError('This business name is already registered');
      }
    } catch {
      // Silent fail - don't block registration if check fails
      setNameExists(false);
    } finally {
      setIsCheckingName(false);
    }
  }, 500);

  const handleBusinessNameChange = useCallback((name: string) => {
    onDataChange({ businessName: name });
    setBusinessNameError('');
    setNameExists(false);
    checkBusinessNameAvailability(name);
  }, [onDataChange, checkBusinessNameAvailability]);

  const validate = (): boolean => {
    let isValid = true;

    // Validate business name
    const businessNameValidation = validators.businessName(data.businessName);
    if (businessNameValidation !== true) {
      setBusinessNameError(businessNameValidation);
      isValid = false;
    } else if (nameExists) {
      setBusinessNameError('This business name is already registered');
      isValid = false;
    } else {
      setBusinessNameError('');
    }

    // Validate vendor type
    const vendorTypeValidation = validators.vendorType(data.vendorTypeID);
    if (vendorTypeValidation !== true) {
      setVendorTypeError(vendorTypeValidation);
      isValid = false;
    } else {
      setVendorTypeError('');
    }

    // Validate phone
    const phoneValidation = validators.phoneNumber(data.phoneNumber);
    if (phoneValidation !== true) {
      setPhoneError(phoneValidation);
      isValid = false;
    } else {
      setPhoneError('');
    }

    // Validate email
    const emailValidation = validators.email(data.email);
    if (emailValidation !== true) {
      setEmailError(emailValidation);
      isValid = false;
    } else {
      setEmailError('');
    }

    return isValid;
  };

  const handleNext = () => {
    if (validate()) {
      onNext();
    }
  };

  // Convert vendor types to select options
  const vendorTypeOptions = vendorTypes
    .filter((type) => type.typeID != null)
    .map((type) => ({
      value: type.typeID.toString(),
      label: type.name,
    }));

  return (
    <Stack gap="md">
      {/* Business name exists error */}
      {nameExists && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Business Name Unavailable"
          color="red"
        >
          <Text size="sm">
            This business name is already registered. Please choose a different name.
          </Text>
        </Alert>
      )}

      <TextInput
        label="Business Name"
        placeholder="Your Company Ltd"
        leftSection={<IconBuilding size={16} />}
        value={data.businessName}
        onChange={(e) => handleBusinessNameChange(e.target.value)}
        error={businessNameError}
        required
        size="md"
        radius="md"
        description="Official registered business name"
        rightSection={isCheckingName ? <Text size="xs" c="dimmed">Checking...</Text> : null}
      />

      <Select
        label="Vendor Type"
        placeholder="Select your vendor type"
        data={vendorTypeOptions}
        value={data.vendorTypeID ? data.vendorTypeID.toString() : null}
        onChange={(value) => {
          onDataChange({ vendorTypeID: value ? parseInt(value, 10) : 0 });
          setVendorTypeError('');
        }}
        error={vendorTypeError}
        required
        size="md"
        radius="md"
        disabled={loadingTypes}
        searchable
        nothingFoundMessage="No vendor types found"
      />

      <Group grow>
        <TextInput
          label="Business Phone"
          placeholder="+27 12 345 6789"
          leftSection={<IconPhone size={16} />}
          value={data.phoneNumber}
          onChange={(e) => {
            const formatted = formatPhoneNumber(e.target.value);
            onDataChange({ phoneNumber: formatted });
            setPhoneError('');
          }}
          error={phoneError}
          required
          size="md"
          radius="md"
          maxLength={17}
        />

        <TextInput
          label="Business Email"
          placeholder="contact@company.com"
          leftSection={<IconMail size={16} />}
          value={data.email}
          onChange={(e) => {
            onDataChange({ email: e.target.value });
            setEmailError('');
          }}
          error={emailError}
          required
          size="md"
          radius="md"
        />
      </Group>

      <TextInput
        label="Website"
        placeholder="www.company.com"
        leftSection={<IconWorld size={16} />}
        value={data.website}
        onChange={(e) => onDataChange({ website: e.target.value })}
        onBlur={(e) => {
          const normalized = normalizeWebsiteUrl(e.target.value);
          if (normalized !== e.target.value) {
            onDataChange({ website: normalized });
          }
        }}
        size="md"
        radius="md"
        description="Optional - https:// will be added automatically"
      />

      <Group justify={showBackButton ? "space-between" : "flex-end"} mt="md">
        {showBackButton && onBack && (
          <Button
            variant="default"
            onClick={onBack}
            size="md"
            radius="md"
          >
            Back
          </Button>
        )}
        <Button
          onClick={handleNext}
          size="md"
          radius="md"
          variant="gradient"
          gradient={{ from: 'indigo', to: 'violet' }}
        >
          Next: Review
        </Button>
      </Group>
    </Stack>
  );
}
